import { SettableHotSpotProps } from "krpano";
import { RequireAtLeast } from "./helperModels";

/**
 * KRPano (being XML) cannot support commas in its attributes
 * To get around this we replace the commas with the URL-safe unicode for it
 */
const commaHtmlCode = "&#44;";

export const safelyParseComma = (value?: string): string =>
  value ? value.replaceAll(",", commaHtmlCode) : "";

/**
 * When dynamically adding fields (scenes) to KRPano, we need to set the
 * basic content of the scene so that the rest of the UI has placeholders.
 * // NOTE: TEMPORARY FIX: Load `blank-placeholder.png` as it appears to darken
 *          the screen during the load process (due to the blend)
 * Old value for Sphere `/krpano/Panos/placeholder.jpeg`
 */
export const sceneStartingContent = `
  <image>
    <sphere url="/krpano/Panos/blank-placeholder.png" />
  </image>
  <preview url="/krpano/Panos/blank-placeholder.png" type="sphere" />
  <view hlookat="get:view_settings.hlookat" vlookat="get:view_settings.vlookat" fov="get:view_settings.fov" maxpixelzoom="2" fovmax="get:view_settings.fovmax" />
`;

/**
 * NOTE: Don't forget to call `window.krpano.call("scale_hotspots()");` once
 * all new hotspots have been added
 * NOTE: `additionalProps` should be in all lowercase to avoid issues
 */
export const addKrpanoHotspot = <T = {}>(
  hotspotName: string,
  additionalProps: RequireAtLeast<SettableHotSpotProps, "group"> & T,
  style?: string
): ReturnType<typeof window.krpano.addhotspot> => {
  // Create Hotspot
  const hs = window.krpano.addhotspot(hotspotName);
  // Add style
  if (style) {
    hs.loadstyle(style);
  }

  // Add additional properties
  // `Object.entries` is the absolute bane of my existence
  Object.entries(additionalProps).forEach(([key, value]) => {
    (hs as any)[key] = value;
  });

  // Return to allow additional manipulation if needed
  return hs;
};

export const addKrpanoHotspotLayer = (
  hs: ReturnType<typeof addKrpanoHotspot>,
  points: {
    ath: SettableHotSpotProps["ath"];
    atv: SettableHotSpotProps["atv"];
  }[],
  /**
   * Optional delay (in ms) between adding each point to create a "drawing"
   * effect as the line renders
   *
   * I wouldn't recommend using this much (if at all)
   * For best results, 3-30ms
   */
  drawEffectDelay: number = 0
) => {
  // Create Array
  const boundaryArray = hs.createarray("point");
  // Create Array Points callback
  const callback: Parameters<typeof points.forEach>[0] = (
    point,
    pointIndex
  ) => {
    const newItem = boundaryArray.createItem(pointIndex);
    newItem.ath = point.ath;
    newItem.atv = point.atv;
  };
  let addPoint = callback;
  // Set up `addPoint` if `drawEffectDelay` is used
  if (drawEffectDelay > 0) {
    addPoint = (point, pointIndex, pointsArray) => {
      setTimeout(
        () => callback(point, pointIndex, pointsArray),
        pointIndex * drawEffectDelay
      );
    };
  }

  // Add points
  points.forEach(addPoint);
};
